import { Directive, EventEmitter, Input, OnInit, OnDestroy, HostListener, Output } from '@angular/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, switchMap, map, catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Address, GetAutoPopulateAddressApiResponse } from '@app-shared/models/address-autocomplete.model';
import { GeographicInfoService } from '@origination-shared/services/geographic-info/geographic-info-service';
import { CustomToastService } from '@app-shared/services';

@Directive({
  selector: '[appAddressAutocomplete]',
  exportAs: 'appAddressAutocomplete' 
})
export class AddressAutocompleteDirective implements OnInit, OnDestroy {
  @Input() minChars = 3;
  @Input() debounceMs = 200;
  @Input() country = 'USA';
  @Output() addressSelected = new EventEmitter<Address>();
  @Output() suggestionsUpdated = new EventEmitter<Address[]>();
  suggestions: Address[] = [];
  selectedSuggestionIndex: number = -1;
  private suggestionSubject = new Subject<string>();
  private suggestionSubscription: Subscription;

  constructor(
    private geoInfoService: GeographicInfoService,
    private toastr: CustomToastService
  ) {}

  ngOnInit(): void {
    this.initializeSuggestions();
  }

  private initializeSuggestions(): void {
    this.suggestionSubscription = this.suggestionSubject.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      filter((text) => text && text.trim().length >= this.minChars),
      switchMap((text) => {
        const searchText = text.trim().replace(/[^a-zA-Z0-9\s]/g, '');
        return this.geoInfoService.getAutoPopulateAddress(searchText, this.country).pipe(
          catchError((err: HttpErrorResponse) => {
            console.error('Address Suggestion Error!', err);
            this.toastr.show(['Unable to fetch Address suggestions. Please try again.']);
            return of(null);
          })
        );
      }),
      map((response: GetAutoPopulateAddressApiResponse | null) => {
        return response?.addressList || [];
      })
    ).subscribe((addressList: Address[]) => {
      this.suggestions = addressList;
      this.suggestionsUpdated.emit(addressList);
      this.selectedSuggestionIndex = -1;
    });
  }

  getSuggestions(text: string): void {
    if (!text || text.trim().length < this.minChars) {
      this.suggestions = [];
      this.selectedSuggestionIndex = -1;
      this.suggestionsUpdated.emit([]);
      this.suggestionSubject.next('');
      return;
    }
    this.suggestionSubject.next(text);
    this.selectedSuggestionIndex = -1;
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent): void {
    if (!this.suggestions || this.suggestions.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.selectedSuggestionIndex < this.suggestions.length - 1) {
        this.selectedSuggestionIndex++;
      } else {
        this.selectedSuggestionIndex = 0;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.selectedSuggestionIndex > 0) {
        this.selectedSuggestionIndex--;
      } else {
        this.selectedSuggestionIndex = this.suggestions.length - 1;
      }
    } else if (event.key === 'Enter') {
      if (this.selectedSuggestionIndex >= 0 && this.selectedSuggestionIndex < this.suggestions.length) {
        event.preventDefault();
        this.selectSuggestion(this.suggestions[this.selectedSuggestionIndex]);
      }
    } else if (event.key === 'Escape') {
      this.suggestions = [];
      this.selectedSuggestionIndex = -1;
      this.suggestionsUpdated.emit([]);
    }
  }

  @HostListener('blur')
  handleBlur(): void {
    // Add a small delay to allow click events on suggestions to be processed first
    setTimeout(() => {
      this.suggestions = [];
      this.selectedSuggestionIndex = -1;
      this.suggestionsUpdated.emit([]);
    }, 300);
  }

  selectSuggestion(address: Address): void {
    if (!address) {
      return;
    }
    this.addressSelected.emit(address);
    this.suggestions = [];
    this.selectedSuggestionIndex = -1;
    this.suggestionsUpdated.emit([]);
  }

  setSelectedIndex(index: number): void {
    this.selectedSuggestionIndex = index;
  }

  clearSelectedIndex(): void {
    this.selectedSuggestionIndex = -1;
  }

  ngOnDestroy(): void {
    if (this.suggestionSubscription) {
      this.suggestionSubscription.unsubscribe();
    }
    this.suggestionSubject.complete();
  }
}